import { PinwheelLinkTokenInput } from '@varo/graphql-schema-types';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import useRequestLinkToken from 'shared/hooks/pinwheel/useRequestLinkToken/useRequestLinkToken';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { PinwheelLinkSessionStateInterface } from 'shared/providers/pinwheel/helpers/PinwheelLinkSessionState';
import { solutionTypeForExperience } from 'shared/providers/pinwheel/helpers/pinwheelSolutionType.helpers';
import PinWheelModalWrapper from 'shared/providers/pinwheel/PinwheelModalWrapper';
import {
  PinwheelModalOutcome,
  PinWheelModalWrapperProps,
} from 'shared/providers/pinwheel/PinwheelModalWrapper.helpers';
import theme from 'shared/styles/theme';
import { GenericError } from 'shared/utils/errors/GenericError';

export interface PinwheelContextProps {
  setIsPinwheelLoading?: (flag: boolean) => void;
  initiatePinwheel: (args?: InitiatePinwheelArgs) => void;
  isPinwheelLoading: boolean;
  isPinwheelLaunched?: boolean;
}

export const PinwheelContext = createContext<PinwheelContextProps>({
  setIsPinwheelLoading: () => {},
  initiatePinwheel: () => {},
  isPinwheelLoading: false,
  isPinwheelLaunched: false,
});

export interface PinwheelOutcome {
  success: boolean;
  hasPinwheelErrorEvent?: boolean;
  hasLoginSuccess?: boolean;
  sessionState?: PinwheelLinkSessionStateInterface;
}

type PinwheelOutcomeCallback = (outcome: PinwheelOutcome) => void;

export interface InitiatePinwheelArgs {
  input?: PinwheelLinkTokenInput;

  /**
   * optional source name for analytics events
   */
  sourceName?: string;
  launchBehavior?: {
    delayAfterTokenMs?: number;
  };

  /**
   * invoked on pinwheel exit. reports the outcome status
   * and associated metadata. when this argument is provided,
   * the deprecated `successCallback` and `exitCallback`
   * will be ignored and no side effects will be produced within this provider.
   * instead consumers should pass their desired behavior in this callback.
   */
  onOutcome?: PinwheelOutcomeCallback;

  /**
   * called when the link token request fails.
   * Note: when this argument is provided, no side effects will be produced
   * within this provider. Consumers should handle the error in this callback.
   */
  onLinkTokenError?: (error: GenericError) => void;
}

export const PinwheelContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'pinWheel',
  });

  const { segmentClient } = useAnalyticsConfiguration();

  const {
    requestLinkToken: fetchLinkToken,
    pinwheelState: pinwheelData,
    requestLinkTokenError,
    setInitialPinwheelData,
  } = useRequestLinkToken();

  const { token, linkTokenId } = pinwheelData;

  const outcomeCallbackRef = useRef<PinwheelOutcomeCallback | undefined>(
    undefined,
  );
  const onLinkTokenErrorCallbackRef =
    useRef<(error: GenericError) => void | undefined>(undefined);

  const [modalProps, setModalProps] = useState<
    Partial<PinWheelModalWrapperProps>
  >({});

  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const launchBehaviorRef =
    useRef<InitiatePinwheelArgs['launchBehavior']>(undefined);

  const [isLaunchBlocked, setIsLaunchBlocked] = useState(false);

  const [isPinwheelLoading, setIsPinwheelLoading] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPinwheelLaunched, setIsPinwheelLaunched] = useState<boolean>(false);

  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);

  const closeErrorModal = () => {
    setOpenErrorModal(!openErrorModal);
    setInitialPinwheelData();
  };

  const clearLaunchState = () => {
    setModalProps({});
    outcomeCallbackRef.current = undefined;
    onLinkTokenErrorCallbackRef.current = undefined;
    launchBehaviorRef.current = undefined;
    setIsLaunchBlocked(false);

    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
  };

  const onExit = (outcome: PinwheelModalOutcome) => {
    const {
      isEarlyExit,
      hasPinwheelErrorEvent,
      hasLoginSuccess,
      sessionState,
    } = outcome;
    setIsPinwheelLaunched(false);
    setLoading(false);

    outcomeCallbackRef.current?.({
      success: !isEarlyExit,
      hasPinwheelErrorEvent: Boolean(hasPinwheelErrorEvent),
      hasLoginSuccess: Boolean(hasLoginSuccess),
      sessionState,
    });
  };

  useEffect(() => {
    if (requestLinkTokenError) {
      setLoading(false);

      if (onLinkTokenErrorCallbackRef.current) {
        // when onLinkTokenErrorCallback is provided, don't produce side effects
        onLinkTokenErrorCallbackRef.current(requestLinkTokenError);
        return;
      }

      // This code should be removed once we start normaliasing,
      // We will have to delete error ErrorScreen code from line no 185 as well,
      setOpenErrorModal(true);
    }
  }, [requestLinkTokenError]);

  useEffect(() => {
    if (!token) {
      return;
    }
    const delayMs = launchBehaviorRef.current?.delayAfterTokenMs ?? 0;
    if (delayMs <= 0) {
      setIsLaunchBlocked(false);
      return;
    }
    setIsLaunchBlocked(true);
    delayTimerRef.current = setTimeout(() => {
      setIsLaunchBlocked(false);
    }, delayMs);

    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, [token]);

  const initiatePinwheel = useCallback(
    (args?: InitiatePinwheelArgs) => {
      const { onOutcome, onLinkTokenError, input, sourceName, launchBehavior } =
        args ?? {};

      clearLaunchState();

      if (onOutcome) {
        outcomeCallbackRef.current = onOutcome;
      }
      if (onLinkTokenError) {
        onLinkTokenErrorCallbackRef.current = onLinkTokenError;
      }

      if (input?.experience) {
        const solutionType = solutionTypeForExperience(input?.experience);
        setModalProps((current) => ({ ...current, solutionType }));
      }

      launchBehaviorRef.current = launchBehavior;

      setModalProps((current) => ({ ...current, sourceName }));

      setLoading(true);

      void fetchLinkToken(input);
    },
    [fetchLinkToken],
  );

  const pinWheelModal = (
    <>
      {token && !isLaunchBlocked ? (
        <PinWheelModalWrapper
          {...modalProps}
          linkTokenId={linkTokenId}
          token={token}
          onOpen={() => {
            setLoading(false);
            setIsPinwheelLaunched(true);
          }}
          onAbruptExit={onExit}
          segmentClient={segmentClient}
        />
      ) : null}

      {openErrorModal ? (
        <View style={styles.error}>
          <ErrorScreen
            screenType={ErrorScreenType.Error}
            messageHeadline={translatedText('error.title')}
            messageBody={translatedText('error.body')}
            primaryButton={{
              title: translatedText('error.button'),
              onPress: closeErrorModal,
            }}
          />
        </View>
      ) : null}
    </>
  );
  return (
    <PinwheelContext.Provider
      value={{
        initiatePinwheel,
        isPinwheelLoading: loading || isPinwheelLoading,
        setIsPinwheelLoading,
        isPinwheelLaunched,
      }}
    >
      {children}
      {pinWheelModal}
    </PinwheelContext.Provider>
  );
};

const styles = StyleSheet.create({
  error: {
    backgroundColor: theme.color.background.primary,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
});
